import { useQuery, useMutation } from '@tanstack/react-query';
import { geolocationService } from '../services/geolocationService';
import type { LocationCodeRequest } from '../types/geolocation.types';

/**
 * Hook to fetch countries
 */
export function useCountries() {
  return useQuery({
    queryKey: ['countries'],
    queryFn: () => geolocationService.getCountries(),
    staleTime: 1000 * 60 * 60, // 1 hour - countries don't change often
  });
}

/**
 * Hook to fetch provinces
 */
export function useProvinces() {
  return useQuery({
    queryKey: ['provinces'],
    queryFn: () => geolocationService.getProvinces(),
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Hook to fetch districts by province
 */
export function useDistricts(provinceId: string | undefined) {
  return useQuery({
    queryKey: ['districts', provinceId],
    queryFn: () => geolocationService.getDistricts(provinceId!),
    enabled: !!provinceId, // Only fetch if provinceId is provided
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to fetch townships by district
 */
export function useTownships(districtId: string | undefined) {
  return useQuery({
    queryKey: ['townships', districtId],
    queryFn: () => geolocationService.getTownships(districtId!),
    enabled: !!districtId, // Only fetch if districtId is provided
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to fetch neighborhoods by township
 */
export function useNeighborhoods(townshipId: string | undefined) {
  return useQuery({
    queryKey: ['neighborhoods', townshipId],
    queryFn: () => geolocationService.getNeighborhoods(townshipId!),
    enabled: !!townshipId, // Only fetch if townshipId is provided
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to fetch location code
 */
export function useLocationCode() {
  return useMutation({
    mutationFn: (data: LocationCodeRequest) =>
      geolocationService.getLocationCode(data),
  });
}

/**
 * Hook to fetch gender types (formulario Particular)
 */
export function useGenders() {
  return useQuery({
    queryKey: ['genders'],
    queryFn: () => geolocationService.getGenders(),
    staleTime: 1000 * 60 * 60,
  });
}

/**
 * Hook to fetch nationality types (formulario Particular)
 */
export function useNationalities() {
  return useQuery({
    queryKey: ['nationalities'],
    queryFn: () => geolocationService.getNationalities(),
    staleTime: 1000 * 60 * 60,
  });
}

// `getDefaultCatalogId` vive en `@lib/utils/catalogDefaults` — se movió ahí
// porque ahora lo usan tanto Clientes como Productos (Fase 2).

/**
 * Combined hook for geolocation with cascading dropdowns
 * Fetches countries, provinces, districts, and townships (corregimientos) based on selections
 */
export function useGeolocation(provinceName?: string, districtName?: string) {
  // Normalizar texto eliminando acentos para comparaciones flexibles
  // (el payload de DGI puede venir sin acentos por normalizeAccents del forward mapper)
  const normalize = (text: string) => text?.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase() || '';

  // Fetch all countries
  const { data: countries, isLoading: isLoadingCountries } = useCountries();

  // Fetch all provinces
  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();

  // Find the selected province ID by name (con fallback por nombre sin acentos)
  const selectedProvince = provinces?.find(
    (p: any) => p.Name === provinceName
  ) || provinces?.find(
    (p: any) => provinceName && normalize(p.Name) === normalize(provinceName)
  );
  const provinceId = selectedProvince?.ProvinceId;

  // Fetch districts for the selected province
  const { data: districts, isLoading: isLoadingDistricts } = useDistricts(provinceId);

  // Find the selected district ID by name (con fallback por nombre sin acentos)
  const selectedDistrict = districts?.find(
    (d: any) => d.Name === districtName
  ) || districts?.find(
    (d: any) => districtName && normalize(d.Name) === normalize(districtName)
  );
  const districtId = selectedDistrict?.DistrictId;

  // Fetch townships (corregimientos) for the selected district
  const { data: townships, isLoading: isLoadingTownships } = useTownships(districtId);

  return {
    countries,
    provinces,
    districts,
    townships,
    isLoadingCountries,
    isLoadingProvinces,
    isLoadingDistricts,
    isLoadingTownships,
  };
}
